"use client";

import React, { useState, useEffect } from "react";
import {
  MapPin,
  Globe,
  Phone,
  Mail,
  X,
  ExternalLink,
  Building,
  Navigation,
  Users,
  ChevronRight,
  BadgeCheck,
  Eye,
  FileText,
} from "lucide-react";
import dynamic from "next/dynamic";
import "leaflet/dist/leaflet.css";

// Fix Leaflet default icons in Next.js (client-side only)
const fixLeafletIcons = () => {
  if (typeof window !== "undefined") {
    const L = require("leaflet");
    delete (L.Icon.Default.prototype as any)._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl:
        "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
      iconUrl:
        "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
      shadowUrl:
        "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
    });
  }
};

// Dynamic imports for react-leaflet (no SSR)
const MapContainer = dynamic(
  () => import("react-leaflet").then((mod) => mod.MapContainer),
  { ssr: false }
);
const TileLayer = dynamic(
  () => import("react-leaflet").then((mod) => mod.TileLayer),
  { ssr: false }
);
const Marker = dynamic(
  () => import("react-leaflet").then((mod) => mod.Marker),
  { ssr: false }
);
const Popup = dynamic(
  () => import("react-leaflet").then((mod) => mod.Popup),
  { ssr: false }
);

interface Company {
  logo: string;
  name: string;
  address: {
    street: string;
    city: string;
    state: string;
    country: string;
    zipCode: string;
  };
  website: string;
  contacts: {
    phone: string;
    email: string;
  };
  mainLanguage: string;
  tpnBadge: {
    type: string;
    status: string;
    proofDocument: string;
    proofDocumentUrl?: string;
    approvedAt: string | null;
    submittedAt: string;
  };
}

interface User {
  _id: string;
  name: string;
  email: string;
  company: Company;
}

// Company map component
const ModernCompanyMap = ({
  name,
  address,
}: {
  name: string;
  address: Company["address"];
}) => {
  const [coords, setCoords] = useState<{ lat: number; lon: number } | null>(
    null
  );
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    // Apply Leaflet icon fixes when component mounts
    fixLeafletIcons();
    setMapReady(true);
  }, []);

  useEffect(() => {
    const fetchCoords = async () => {
      const fullAddress = `${address.street}, ${address.city}, ${address.state}, ${address.country}`;
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
            fullAddress
          )}`
        );
        const data = await response.json();
        if (data && data.length > 0) {
          setCoords({
            lat: parseFloat(data[0].lat),
            lon: parseFloat(data[0].lon),
          });
        }
      } catch (error) {
        console.error("Error geocoding address:", error);
      }
    };
    
    if (mapReady) {
      fetchCoords();
    }
  }, [address, mapReady]);

  if (!mapReady || !coords) {
    return (
      <div className="h-80 w-full rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-yellow-500 border-t-transparent mx-auto mb-2"></div>
          <div className="text-gray-500 text-sm">Loading map...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-80 w-full rounded-2xl overflow-hidden shadow-lg border border-gray-200 relative z-0">
      <MapContainer
        center={[coords.lat, coords.lon]}
        zoom={15}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={false}
        className="rounded-2xl"
      >
        <TileLayer
          attribution='© <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        />
        <Marker position={[coords.lat, coords.lon]}>
          <Popup className="custom-popup">
            <div className="font-bold text-gray-900">{name}</div>
            <div className="text-sm text-gray-600 mt-1">
              {address.street}, {address.city}
            </div>
          </Popup>
        </Marker>
      </MapContainer>
    </div>
  );
};

// Component for proof document viewing
const ProofDocumentViewer = ({ 
  proofDocument, 
  proofDocumentUrl,
  companyName 
}: { 
  proofDocument: string;
  proofDocumentUrl?: string;
  companyName: string;
}) => {
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Use proofDocumentUrl if available, otherwise use proofDocument
  const imageUrl = proofDocumentUrl || proofDocument;

  if (!imageUrl) {
    return (
      <div className="bg-gray-800 rounded-2xl p-6 text-center">
        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-300 text-sm">No proof document available</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-gray-800 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-yellow-500" />
          Certification Proof
        </h3>
        
        <div className="space-y-4">
          {/* Image preview */}
          <div 
            className="relative bg-gray-700 rounded-xl border-2 border-dashed border-gray-600 p-4 cursor-pointer hover:border-yellow-400 transition-colors group"
            onClick={() => setIsViewerOpen(true)}
          >
            <div className="flex flex-col items-center justify-center min-h-[120px]">
              {!imageLoaded && !imageError && (
                <div className="text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-yellow-500 border-t-transparent mx-auto mb-2"></div>
                  <p className="text-gray-300 text-sm">Loading document...</p>
                </div>
              )}
              
              {imageError ? (
                <div className="text-center">
                  <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-300 text-sm">Document preview not available</p>
                  <p className="text-gray-400 text-xs mt-1">Click to try viewing</p>
                </div>
              ) : (
                <img
                  src={imageUrl}
                  alt={`TPN Certification Proof for ${companyName}`}
                  className={`max-w-full max-h-32 object-contain rounded-lg ${imageLoaded ? 'block' : 'hidden'}`}
                  onLoad={() => setImageLoaded(true)}
                  onError={() => setImageError(true)}
                />
              )}
            </div>
            
            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 rounded-xl transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
              <div className="bg-yellow-500 rounded-full p-2 shadow-lg">
                <Eye className="w-5 h-5 text-black" />
              </div>
            </div>
          </div>

          {/* View button */}
          <button
            onClick={() => setIsViewerOpen(true)}
            className="w-full flex items-center justify-center gap-2 bg-yellow-500 text-black py-3 px-4 rounded-lg font-medium hover:bg-yellow-400 transition-colors"
          >
            <Eye className="w-4 h-4" />
            View Document
          </button>
        </div>
      </div>

      {/* Full view modal - Same pattern as main modal */}
      {isViewerOpen && (
        <div className="fixed inset-0 bg-white bg-opacity-50 flex items-center justify-center z-[100] p-4 backdrop-blur-sm">
          <div className="bg-black rounded-3xl max-w-4xl w-full max-h-[90vh] flex flex-col shadow-2xl">
            {/* Modal header */}
            <div className="sticky top-0 bg-black border-b border-gray-700 rounded-t-3xl p-6 flex justify-between items-start z-10">
              <div>
                <h2 className="text-2xl font-bold text-white">
                  TPN Certification Proof
                </h2>
                <p className="text-gray-300 text-sm mt-1">{companyName}</p>
              </div>
              <button
                onClick={() => setIsViewerOpen(false)}
                className="p-2 hover:bg-gray-800 rounded-xl transition-colors"
              >
                <X className="w-6 h-6 text-gray-300" />
              </button>
            </div>

            {/* Image content */}
            <div className="flex-1 overflow-auto p-8 flex items-center justify-center">
              {imageError ? (
                <div className="text-center">
                  <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-300 text-lg">Failed to load document</p>
                  <p className="text-gray-400 text-sm mt-2">
                    The document may be unavailable or corrupted.
                  </p>
                </div>
              ) : (
                <div className="relative w-full flex justify-center">
                  {!imageLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-2 border-yellow-500 border-t-transparent"></div>
                    </div>
                  )}
                  <img
                    src={imageUrl}
                    alt={`TPN Certification Proof for ${companyName}`}
                    className={`max-w-full max-h-[60vh] object-contain rounded-lg ${imageLoaded ? 'block' : 'invisible'}`}
                    onLoad={() => setImageLoaded(true)}
                    onError={() => setImageError(true)}
                  />
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-700 bg-gray-900 rounded-b-3xl">
              <div className="flex justify-between items-center">
                <span className="text-gray-300 text-sm">TPN Certification Document</span>
                <button
                  onClick={() => setIsViewerOpen(false)}
                  className="bg-gray-700 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export const Associates = () => {
  const [approvedCompanies, setApprovedCompanies] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<User | null>(null);

  useEffect(() => {
    fetchApprovedCompanies();
  }, []);

  const fetchApprovedCompanies = async () => {
    try {
      // Public request without token
      const response = await fetch("/api/companies");

      if (response.ok) {
        const users: User[] = await response.json();
        const approved = users.filter(
          (user) =>
            user.company &&
            user.company.tpnBadge &&
            user.company.tpnBadge.status === "approved" &&
            user.company.name &&
            user.company.name.trim() !== ""
        );
        setApprovedCompanies(approved);
      } else {
        console.error("Error fetching companies:", response.status);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate companies to display
  const companiesToShow = showAll
    ? approvedCompanies
    : approvedCompanies.slice(0, 8);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-yellow-500 border-t-transparent mx-auto mb-4"></div>
          <div className="text-gray-600">Loading associates...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white py-20 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-black rounded-3xl p-12 text-center mb-20 shadow-2xl">
          <div className="inline-flex items-center gap-2 bg-yellow-500 text-black px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Users className="w-4 h-4" />
            Our Global Network
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-yellow-500 mb-6">
            <span className="text-yellow-500 ">Associates</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
            Certified companies that share our commitment to excellence and
            innovation in the global market.
          </p>
        </div>

        {/* Company list */}
        {approvedCompanies.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-24 h-24 bg-gray-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
              <Building className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-3">
              No associates found
            </h3>
            <p className="text-gray-600 max-w-md mx-auto">
              Approved companies will appear here once they are validated.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-16">
              {companiesToShow.map((user: User) => (
                <div
                  key={user._id}
                  onClick={() => setSelectedCompany(user)}
                  className="cursor-pointer group"
                >
                  <div className="bg-white rounded-3xl p-6 border border-gray-200 hover:border-yellow-400 hover:shadow-2xl transition-all duration-300 h-full flex flex-col relative overflow-hidden">
                    {/* TPN Badge */}
                    <div className="absolute top-4 right-4 z-10">
                      <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg">
                        <BadgeCheck className="w-3 h-3" />
                        TPN
                      </div>
                    </div>

                    <div className="flex justify-center mb-5 relative">
                      <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center shadow-lg border border-gray-100">
                        {user.company.logo ? (
                          <img
                            src={user.company.logo}
                            alt={user.company.name}
                            className="w-12 h-12 object-contain"
                          />
                        ) : (
                          <Building className="w-8 h-8 text-gray-400" />
                        )}
                      </div>
                    </div>
                    
                    <h3 className="text-lg font-semibold text-center mb-3 line-clamp-2 text-gray-900">
                      {user.company.name}
                    </h3>
                    
                    <div className="flex items-center justify-center text-gray-600 text-sm mt-auto pt-4 border-t border-gray-100">
                      <MapPin className="w-4 h-4 mr-2 text-yellow-500" />
                      <span className="font-medium">
                        {user.company.address.city}
                      </span>
                    </div>

                    {/* TPN Badge Type */}
                    <div className="mt-3 text-center">
                      <span className="inline-block bg-blue-50 text-blue-700 px-2 py-1 rounded-full text-xs font-medium">
                        {user.company.tpnBadge.type || "TPN Certified"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Show more button */}
            {approvedCompanies.length > 8 && (
              <div className="text-center">
                <button
                  onClick={() => setShowAll(!showAll)}
                  className="inline-flex items-center gap-3 bg-black text-white px-8 py-4 rounded-2xl font-semibold hover:bg-gray-900 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  {showAll
                    ? "Show Less"
                    : `View All (${approvedCompanies.length})`}
                  <ChevronRight
                    className={`w-5 h-5 transition-transform ${
                      showAll ? "rotate-90" : ""
                    }`}
                  />
                </button>
              </div>
            )}
          </>
        )}

        {/* Details modal */}
        {selectedCompany && (
          <div className="fixed inset-0 bg-white bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-black rounded-3xl max-w-6xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
              <div className="sticky top-0 bg-black border-b border-gray-700 rounded-t-3xl p-6 flex justify-between items-start z-10">
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24  rounded-2xl flex items-center justify-center shadow-lg">
                    {selectedCompany.company.logo ? (
                      <img
                        src={selectedCompany.company.logo}
                        alt={selectedCompany.company.name}
                        className="w-10 h-10 object-contain"
                      />
                    ) : (
                      <Building className="w-8 h-8 text-gray-400" />
                    )}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">
                      {selectedCompany.company.name}
                    </h2>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                        <BadgeCheck className="w-3 h-3" />
                        TPN CERTIFIED
                      </span>
                      <span className="bg-green-900 text-green-300 px-2 py-1 rounded-full text-xs font-medium">
                        ✓ Verified
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedCompany(null)}
                  className="p-2 hover:bg-gray-800 rounded-xl transition-colors"
                >
                  <X className="w-6 h-6 text-gray-300" />
                </button>
              </div>

              <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Column 1: Contact information */}
                <div className="space-y-6">
                  <div className="bg-gray-800 rounded-2xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <Phone className="w-5 h-5 text-yellow-500" />
                      Contact Information
                    </h3>
                    {selectedCompany.company.contacts.phone && (
                      <p className="text-gray-300">{selectedCompany.company.contacts.phone}</p>
                    )}
                    {selectedCompany.company.contacts.email && (
                      <p className="text-gray-300">{selectedCompany.company.contacts.email}</p>
                    )}
                    {selectedCompany.company.website && (
                      <a
                        href={selectedCompany.company.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-yellow-400 hover:text-yellow-300 flex items-center gap-1 transition-colors"
                      >
                        {selectedCompany.company.website}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>

                  <div className="bg-gray-800 rounded-2xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-yellow-500" />
                      Address
                    </h3>
                    <p className="text-gray-300">{selectedCompany.company.address.street}</p>
                    <p className="text-gray-300">
                      {selectedCompany.company.address.city},{" "}
                      {selectedCompany.company.address.state}
                    </p>
                    <p className="text-gray-300">
                      {selectedCompany.company.address.zipCode} •{" "}
                      {selectedCompany.company.address.country}
                    </p>
                  </div>
                </div>

                {/* Column 2: TPN information and Proof Document */}
                <div className="space-y-6">
                  {/* TPN Badge Information */}
                  <div className=" bg-opacity-10 rounded-2xl p-6 border border-yellow-500 border-opacity-20">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <BadgeCheck className="w-5 h-5 text-yellow-400" />
                      TPN Certification
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-300">Type:</span>
                        <span className="font-medium text-white">
                          {selectedCompany.company.tpnBadge.type || "Standard"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Status:</span>
                        <span className="font-medium text-green-400">
                          ✓ Approved
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Certified Since:</span>
                        <span className="font-medium text-white">
                          {selectedCompany.company.tpnBadge.approvedAt 
                            ? new Date(selectedCompany.company.tpnBadge.approvedAt).toLocaleDateString()
                            : "Recently"
                          }
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Proof Document Viewer */}
                  <ProofDocumentViewer
                    proofDocument={selectedCompany.company.tpnBadge.proofDocument}
                    proofDocumentUrl={selectedCompany.company.tpnBadge.proofDocumentUrl}
                    companyName={selectedCompany.company.name}
                  />
                </div>

                {/* Column 3: Map */}
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Navigation className="w-5 h-5 text-yellow-500" />
                    Location
                  </h3>
                  <ModernCompanyMap
                    name={selectedCompany.company.name}
                    address={selectedCompany.company.address}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};